import { useEffect } from 'react';
import { buildConfigurationItemId } from './helpers';
import DocumentationCardPaywallBlock from '../building-blocks/documentation-card/DocumentationCardPaywallBlock';
import { UI_EVENTS } from '../../../services/events';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import usePaywall from '../../dashboard/paywall/usePaywall';
import { DocumentationItem } from '@server/documentation';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { useSetQueryParamsOnMount } from '../../../navigation-utils/useSetQueryParamsOnMount';
import { useLocation } from 'react-router';
import { appRoutes } from '../../../constants/appRoutes';
import { DocumentationCardShell } from '../building-blocks/documentation-card/DocumentationCardShell';

interface DependenciesSideDialogContentProps {
  crmOrgId: string;
  item: DocumentationItem;
  type: string;
  onClose: () => void;
  children: React.ReactNode;
  isReadOnly?: boolean;
  headerContent?: React.ReactNode;
  color?: string;
  isPaywalled?: boolean;
  paywallObjectName?: string;
  onCleanup?: () => void;
}

export const DependenciesSideDialogContent = ({
  item,
  type,
  onClose,
  children,
  isReadOnly = false,
  headerContent,
  color,
  isPaywalled: isPaywalledProp,
  paywallObjectName,
  onCleanup,
}: DependenciesSideDialogContentProps) => {
  const { isPaywallActive } = usePaywall();
  const isPaywall = isPaywalledProp ?? false;
  const showPaywall = isPaywallActive && isPaywall;
  const { pathname } = useLocation();

  const isOrgMigration = pathname.startsWith(appRoutes.orgMigration.route);
  const bypassEntirely = !item.parentType || isReadOnly || isOrgMigration;

  useSetQueryParamsOnMount(
    {
      [DeepLinkSearchParams.configurationItemId]: buildConfigurationItemId({
        parentType: item.parentType || '',
        name: item.name,
        id: item.id,
      }),
    },
    bypassEntirely,
  );

  const sendBiEvent = useSendBiEvent();
  useEffect(() => {
    const props = {
      element: type,
      object: item.name,
    };
    sendBiEvent({ name: UI_EVENTS.documentationOpenCard, props });
  }, [sendBiEvent, item.name, type]);

  useEffect(() => {
    return () => {
      onCleanup?.();
    };
  }, [onCleanup]);

  return (
    <DocumentationCardShell
      onClose={onClose}
      headerContent={!showPaywall ? headerContent : undefined}
      color={color}
    >
      {showPaywall && <DocumentationCardPaywallBlock objectName={paywallObjectName ?? ''} />}
      {!showPaywall && children}
    </DocumentationCardShell>
  );
};
